import { TranslateService } from '@ngx-translate/core';
import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { ParkingRequestClientService } from '@services/api/parking-request/parking-request-client.service';
import { IParkingRequestModel } from '@services/api/parking-request/parking-request-model.interface';
import { LogService } from '@services/util/log.service';
import { MessageService } from 'primeng/api';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { ParkingRequestFormMappingService } from './parking-request-form-mapping.service';
import { ParkingRequestFormValidationService } from './parking-request-form-validation.service';

/**
 * Service to handle form-specific API operations for parking requests
 * Wraps existing client services with form-specific logic and error handling
 */
@Injectable({
  providedIn: 'root'
})
export class ParkingRequestFormService {
  private readonly parkingRequestClient = inject(ParkingRequestClientService);
  private readonly bookingClient = inject(BookingClientService);
  private readonly mappingService = inject(ParkingRequestFormMappingService);
  private readonly validationService = inject(ParkingRequestFormValidationService);
  private readonly log = inject(LogService);
  private readonly translate = inject(TranslateService);

  /**
   * Save parking request (create or update based on editMode)
   * @param formData Form data to save
   * @param editMode Whether this is an edit operation
   * @param parkingRequestId ID for update operations
   * @param messageService Message service for user feedback
   * @returns Observable with the saved parking request
   */
  saveParkingRequest(
    formData: any,
    editMode: boolean,
    parkingRequestId: string | null,
    messageService: MessageService
  ): Observable<IParkingRequestModel> {
    const parkingRequestData = this.mappingService.mapFormToModel(formData, parkingRequestId || undefined);

    if (editMode && parkingRequestId) {
      // Update existing parking request
      const updateData = { ...parkingRequestData, id: parseInt(parkingRequestId) };

      return this.parkingRequestClient.updateItem(updateData).pipe(
        map((updatedRequest) => {
          messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.success'),
            detail: this.translate.instant('parking.form.saveSuccess')
          });
          return updatedRequest;
        }),
        catchError((error: Error) => {
          this.log.error('Failed to update parking request', error);
          messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.error'),
            detail: this.translate.instant('parking.form.saveFailed')
          });
          throw error;
        })
      );
    } else {
      // Create new parking request
      return this.parkingRequestClient.createItem(parkingRequestData).pipe(
        map((newRequest) => {
          messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.success'),
            detail: this.translate.instant('parking.form.saveSuccess')
          });
          return newRequest;
        }),
        catchError((error: Error) => {
          this.log.error('Failed to create parking request', error);
          messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.error'),
            detail: this.translate.instant('parking.form.createFailed')
          });
          throw error;
        })
      );
    }
  }

  /**
   * Load parking request for editing
   * @param parkingRequestId ID of the parking request to load
   * @param messageService Message service for error feedback
   * @returns Observable with the parking request data
   */
  loadParkingRequestForEdit(parkingRequestId: string, messageService: MessageService): Observable<IParkingRequestModel> {
    // Validate that the ID is a pure numeric string
    if (!/^\d+$/.test(parkingRequestId)) {
      // Invalid ID format - treat as 404
      const invalidIdError = {
        isParkingRequest404: true,
        status: 404,
        message: 'Invalid parking request ID format',
        originalError: new Error(`Invalid ID format: ${parkingRequestId}`)
      };
      return throwError(() => invalidIdError);
    }

    // Parse numeric ID string
    const id = parseInt(parkingRequestId, 10);

    return this.parkingRequestClient.getItem(id).pipe(
      catchError((error: any) => {
        this.log.error('Failed to load parking request', error);

        // Check if this is a 404 error (parking request not found)
        // Handle both direct HTTP errors and wrapped errors
        const is404Error =
          error?.status === 404 ||
          error?.error?.status === 404 ||
          (error?.message && error.message.includes('404')) ||
          error?.originalError?.status === 404 ||
          // Check if the error is from a failed HTTP request that resulted in 404
          (typeof error === 'object' && error !== null && (error.toString().includes('404') || error.name === 'HttpErrorResponse'));

        if (is404Error) {
          // For 404 errors, create a clean error object that the container can handle
          // Don't show toast message for 404 - let the container handle navigation
          const notFoundError = {
            isParkingRequest404: true,
            status: 404,
            message: 'Parking request not found',
            originalError: error
          };
          throw notFoundError;
        } else {
          // For other errors, show the standard error toast
          messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.error'),
            detail: this.translate.instant('parking.form.loadError')
          });
          throw error;
        }
      })
    );
  }

  /**
   * Load booking details for form
   * @param bookingId ID of the booking to load
   * @param messageService Message service for error feedback
   * @returns Observable with the booking data
   */
  loadBookingDetails(bookingId: string, messageService: MessageService): Observable<IBookingModel> {
    const id = parseInt(bookingId.split('/').pop() || '0', 10);

    return this.bookingClient.getItem(id).pipe(
      catchError((error: Error) => {
        this.log.error('Failed to load booking details', error);
        messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.error'),
          detail: this.translate.instant('parking.form.loadBookingError')
        });
        throw error;
      })
    );
  }

  /**
   * Load booking for parking request with validation
   * @param bookingId ID of the booking to load
   * @param messageService Message service for feedback
   * @returns Observable with the booking data
   */
  loadBookingForParkingRequest(bookingId: string, messageService: MessageService): Observable<IBookingModel> {
    const id = parseInt(bookingId.split('/').pop() || '0', 10);

    return this.bookingClient.getItem(id).pipe(
      map((booking: IBookingModel) => {
        if (booking && booking.end_date) {
          const endDate = new Date(booking.end_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (endDate < today) {
            messageService.add({
              severity: 'warn',
              summary: this.translate.instant('common.warning'),
              detail: this.translate.instant('parking.form.bookingEndDatePassed')
            });
          }
        }
        return booking;
      }),
      catchError((error: Error) => {
        this.log.error('Failed to load booking', error);
        messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.error'),
          detail: this.translate.instant('parking.form.loadBookingError')
        });
        throw error;
      })
    );
  }
}
